import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Upload, FileSpreadsheet, Check, RefreshCw, Building2, AlertCircle, 
  Plus, TrendingUp, TrendingDown, ArrowRightLeft, DollarSign, Wallet,
  PieChart, CreditCard, Landmark
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend
} from "recharts";

const COLORS = ['#2E5C46', '#4A8F76', '#78B89A', '#FFB347', '#FF6B6B', '#A6D4C3', '#6366F1'];

export default function BankImportPage() {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [imports, setImports] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedMatches, setSelectedMatches] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const expenseCategories = useMemo(
    () => (analysis?.by_category || []).filter((c) => c.expense > 0),
    [analysis]
  );
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({
    name: "",
    account_type: "transaction",
    bank_name: "",
    account_number_last4: ""
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [importsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/bank/imports`, { headers }),
        axios.get(`${API}/bank/accounts`, { headers })
      ]);
      setImports(importsRes.data);
      setAccounts(accountsRes.data);
      
      // Fetch analysis
      const analysisRes = await axios.get(`${API}/bank/analysis`, { headers });
      setAnalysis(analysisRes.data);
      
      // Fetch recent transactions
      const txnRes = await axios.get(`${API}/bank/transactions`, { headers });
      setTransactions(txnRes.data.slice(0, 50));
    } catch (error) {
      console.error("Failed to fetch data");
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const name = selectedFile.name.toLowerCase();
      if (name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls')) {
        setFile(selectedFile);
        setImportResult(null);
      } else {
        toast.error("Please select a CSV or Excel file");
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedAccount) {
        formData.append('account_id', selectedAccount);
      }

      const response = await axios.post(`${API}/bank/smart-import`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      // Pre-select high confidence matches
      const preSelected = {};
      response.data.invoice_matches?.forEach((match, idx) => {
        if (match.confidence === 'high') {
          preSelected[idx] = true;
        }
      });
      setSelectedMatches(preSelected);
      
      toast.success(`Imported ${response.data.summary.transactions_imported} transactions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const toggleMatch = (idx) => {
    setSelectedMatches(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const applyMatches = async () => {
    const matchesToApply = importResult?.invoice_matches
      ?.filter((_, idx) => selectedMatches[idx])
      ?.map(match => ({
        invoice_id: match.invoice_id,
        transaction_id: match.transaction_id,
        payment_date: match.transaction_date
      })) || [];

    if (matchesToApply.length === 0) {
      toast.error("No matches selected");
      return;
    }

    try {
      const response = await axios.post(`${API}/bank/apply-matches`, matchesToApply, { headers });
      toast.success(`Updated ${response.data.updated_count} invoices to paid`);
      setImportResult(null);
      setFile(null);
      setSelectedMatches({});
      fetchData();
    } catch (error) {
      toast.error("Failed to apply matches");
    }
  };

  const createAccount = async () => {
    try {
      await axios.post(`${API}/bank/accounts`, newAccount, { headers });
      toast.success("Account created");
      setAccountDialogOpen(false);
      setNewAccount({ name: "", account_type: "transaction", bank_name: "", account_number_last4: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create account");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'income': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'expense': return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'transfer_in': return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
      case 'transfer_out': return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'credit_card': return <CreditCard className="w-4 h-4" />;
      case 'savings': return <Wallet className="w-4 h-4" />;
      default: return <Landmark className="w-4 h-4" />;
    }
  };

  return (
    <DashboardLayout title="Bank Data Import">
      <div className="space-y-6" data-testid="bank-import-page">
        <Tabs defaultValue="import" className="space-y-4">
          <TabsList>
            <TabsTrigger value="import">Import Data</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="analysis">Analysis</TabsTrigger>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
          </TabsList>

          {/* Import Tab */}
          <TabsContent value="import" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Smart Bank Data Import
                </CardTitle>
                <CardDescription>
                  Upload bank statements from any bank (CommBank, ANZ, Westpac, NAB, Amex, etc.) in CSV or Excel format. 
                  Our AI will automatically detect columns and understand your data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                  <div className="flex gap-4 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Bank Account (Optional)</Label>
                    <Select value={selectedAccount || "auto"} onValueChange={(v) => setSelectedAccount(v === "auto" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auto-detect or select existing account" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto-detect new account</SelectItem>
                        {accounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.name} ({acc.bank_name})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* File Upload */}
                <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">
                      Drop your bank statement here or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Supports CSV, XLSX, XLS from any bank
                    </p>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="max-w-xs mx-auto"
                      data-testid="bank-file-input"
                    />
                    {file && (
                      <Badge variant="secondary" className="mt-2">
                        <FileSpreadsheet className="w-3 h-3 mr-1" />
                        {file.name}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Features Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-1">🤖 AI-Powered Detection</h4>
                    <p className="text-xs text-muted-foreground">
                      Automatically detects date, description, amount columns from any format
                    </p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-1">🔄 Transfer Detection</h4>
                    <p className="text-xs text-muted-foreground">
                      Identifies transfers between accounts to avoid double counting
                    </p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-1">🧾 Invoice Matching</h4>
                    <p className="text-xs text-muted-foreground">
                      Auto-matches payments to outstanding invoices
                    </p>
                  </div>
                </div>

                <Button 
                  onClick={handleImport} 
                  disabled={!file || importing}
                  className="w-full"
                  size="lg"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing & Importing...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Import & Analyze Data
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Import Results */}
            {importResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    Import Complete
                  </CardTitle>
                  <CardDescription>
                    Detected bank: <Badge variant="outline">{importResult.account?.bank}</Badge>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold">{importResult.summary.transactions_imported}</p>
                      <p className="text-xs text-muted-foreground">Imported</p>
                    </div>
                    <div className="text-center p-3 bg-green-100 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(importResult.summary.income_total)}
                      </p>
                      <p className="text-xs text-muted-foreground">Income</p>
                    </div>
                    <div className="text-center p-3 bg-red-100 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency(importResult.summary.expense_total)}
                      </p>
                      <p className="text-xs text-muted-foreground">Expenses</p>
                    </div>
                    <div className="text-center p-3 bg-blue-100 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {importResult.summary.transfers_detected}
                      </p>
                      <p className="text-xs text-muted-foreground">Transfers</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-100 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-600">
                        {importResult.summary.duplicates_skipped}
                      </p>
                      <p className="text-xs text-muted-foreground">Duplicates Skipped</p>
                    </div>
                  </div>

                  {/* Column Mapping */}
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">Detected Column Mapping:</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(importResult.column_mapping).map(([key, value]) => (
                        value && (
                          <Badge key={key} variant="outline">
                            {key}: {value}
                          </Badge>
                        )
                      ))}
                    </div>
                  </div>

                  {/* Invoice Matches */}
                  {importResult.invoice_matches?.length > 0 && (
                    <>
                      <h4 className="font-medium">Invoice Matches Found</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">Apply</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Confidence</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importResult.invoice_matches.map((match, idx) => (
                            <TableRow key={match.transaction_id || `m-${idx}`}>
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedMatches[idx] || false}
                                  onChange={() => toggleMatch(idx)}
                                  className="w-4 h-4 rounded"
                                />
                              </TableCell>
                              <TableCell>{match.transaction_date}</TableCell>
                              <TableCell className="max-w-[150px] truncate">
                                {match.transaction_description}
                              </TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                {formatCurrency(match.transaction_amount)}
                              </TableCell>
                              <TableCell className="font-medium">{match.invoice_number}</TableCell>
                              <TableCell>{match.client}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className={
                                  match.confidence === 'high' 
                                    ? "bg-green-100 text-green-800" 
                                    : "bg-yellow-100 text-yellow-800"
                                }>
                                  {match.confidence}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      <div className="flex justify-end">
                        <Button onClick={applyMatches} data-testid="apply-matches-btn">
                          <Check className="w-4 h-4 mr-2" />
                          Apply {Object.values(selectedMatches).filter(Boolean).length} Matches
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Errors */}
                  {importResult.errors?.length > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Import Warnings ({importResult.errors.length})
                      </h4>
                      <ul className="text-xs text-red-700 space-y-1">
                        {importResult.errors.map((err, idx) => (
                          <li key={`err-${idx}-${String(err).slice(0, 20)}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Import History */}
            {imports.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Import History</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>File</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead className="text-right">Imported</TableHead>
                        <TableHead className="text-right">Matches</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {imports.slice(0, 10).map((imp) => (
                        <TableRow key={imp.id || imp.imported_at}>
                          <TableCell>
                            {new Date(imp.imported_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="truncate max-w-[150px]">{imp.filename}</TableCell>
                          <TableCell>{imp.account_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{imp.bank_detected || 'Unknown'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{imp.transactions_imported || imp.total_transactions}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{imp.invoice_matches || imp.matched_invoices || 0}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Latest imported bank transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Account</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((txn, idx) => (
                        <TableRow key={txn.id || `txn-${idx}`}>
                          <TableCell>{txn.date}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {getTransactionTypeIcon(txn.transaction_type)}
                              <span className="text-xs capitalize">{txn.transaction_type?.replace('_', ' ')}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{txn.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{txn.category}</Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(txn.amount)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{txn.account_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No transactions imported yet</p>
                    <p className="text-sm">Upload a bank statement to get started</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-4">
            {analysis && analysis.transaction_count > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Income</p>
                          <p className="text-xl font-bold text-green-600">{formatCurrency(analysis.total_income)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Expenses</p>
                          <p className="text-xl font-bold text-red-600">{formatCurrency(analysis.total_expenses)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${analysis.net_cash_flow >= 0 ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center`}>
                          <DollarSign className={`w-5 h-5 ${analysis.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Net Cash Flow</p>
                          <p className={`text-xl font-bold ${analysis.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(analysis.net_cash_flow)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Transactions</p>
                          <p className="text-xl font-bold">{analysis.transaction_count}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Monthly Cash Flow</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analysis.by_month?.length > 0 && (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={analysis.by_month}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Legend />
                            <Bar dataKey="income" fill="#2E5C46" name="Income" />
                            <Bar dataKey="expense" fill="#FF6B6B" name="Expenses" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Expenses by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analysis.by_category?.filter(c => c.expense > 0).length > 0 && (
                        <ResponsiveContainer width="100%" height={300}>
                          <RechartsPie>
                            <Pie
                              data={analysis.by_category.filter(c => c.expense > 0)}
                              dataKey="expense"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ category, expense }) => `${category}: ${formatCurrency(expense)}`}
                            >
                              {expenseCategories.map((entry, index) => (
                                <Cell key={`cell-${entry.category}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                          </RechartsPie>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <PieChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No data to analyze yet</p>
                  <p className="text-sm">Import bank statements to see analysis</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Accounts Tab */}
          <TabsContent value="accounts" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Bank Accounts</CardTitle>
                  <CardDescription>Manage your connected bank accounts</CardDescription>
                </div>
                <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Bank Account</DialogTitle>
                      <DialogDescription>
                        Add a new bank account for organizing your imports
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Account Name</Label>
                        <Input
                          placeholder="e.g., CommBank Transaction"
                          value={newAccount.name}
                          onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input
                          placeholder="e.g., CommBank, ANZ, Amex"
                          value={newAccount.bank_name}
                          onChange={(e) => setNewAccount({ ...newAccount, bank_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Type</Label>
                        <Select
                          value={newAccount.account_type}
                          onValueChange={(v) => setNewAccount({ ...newAccount, account_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="transaction">Transaction Account</SelectItem>
                            <SelectItem value="savings">Savings Account</SelectItem>
                            <SelectItem value="credit_card">Credit Card</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Last 4 Digits (Optional)</Label>
                        <Input
                          placeholder="1234"
                          maxLength={4}
                          value={newAccount.account_number_last4}
                          onChange={(e) => setNewAccount({ ...newAccount, account_number_last4: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
                      <Button onClick={createAccount}>Create Account</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {accounts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account Name</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Last 4</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accounts.map((acc) => (
                        <TableRow key={acc.id || acc.name}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {getAccountTypeIcon(acc.account_type)}
                              {acc.name}
                            </div>
                          </TableCell>
                          <TableCell>{acc.bank_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {acc.account_type?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{acc.account_number_last4 || '-'}</TableCell>
                          <TableCell>{new Date(acc.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Landmark className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No accounts added yet</p>
                    <p className="text-sm">Accounts will be auto-created when you import data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
